import {
  Button,
  LightMode,
  Text,
  arrowDownToLineIcon,
  documentCheckmarkIcon,
  documentUploadIcon,
  trashCanIcon,
  visibilityShowIcon,
} from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  ButtonsContainer,
  ConfirmModal,
  ListViewItemWithActions,
  localizeDateAndTime,
} from "@repo/ui";
import { useState } from "react";

import { FileLoadingEnum, getFileIcon } from "../utils";

interface FileListItemProps {
  attachmentId: number;
  fileName: string;
  fileType?: string;
  uploadedDate: string;
  uploadedBy?: string;
  isShared?: boolean;
  status?: { text: string; color?: string } | null;
  editable?: boolean;
  loading?: FileLoadingEnum;
  loadingFileId?: number;
  allowPreview?: boolean;
  onDownload?: (action: "OPEN" | "DOWNLOAD") => void;
  onDelete?: (attachmentId: number) => void;
  onShare?: (attachmentId: number, shared: boolean) => void;
}

export const FileListItem = ({
  attachmentId,
  fileName,
  fileType,
  uploadedDate,
  uploadedBy,
  status,
  allowPreview = true,
  onDownload,
  onDelete,
  editable,
  loading,
  loadingFileId,
  onShare,
  isShared,
}: FileListItemProps) => {
  const { dateFnsLocale } = useTranslation();
  const [showConfirmSharing, setShowConfirmSharing] = useState(false);

  return (
    <>
      <ListViewItemWithActions
        icon={getFileIcon(fileType)}
        title={<Text textStyle="Heading.XS">{fileName}</Text>}
        description={
          status ? (
            <Text
              textStyle="Body.S"
              color={status.color ?? LightMode.colourTextAndIcon3}
            >
              {status.text}
            </Text>
          ) : undefined
        }
        addOn={
          <Text color={LightMode.colourTextAndIcon3} textStyle="Body.S">
            {`${uploadedBy ?? t("label.system")} | ${localizeDateAndTime(uploadedDate, dateFnsLocale)}`}
          </Text>
        }
        action={
          editable ? (
            <ButtonsContainer>
              {onShare && (
                <Button
                  size="small"
                  type="tertiary"
                  iconOnly
                  ssrIcon={
                    isShared ? documentCheckmarkIcon : documentUploadIcon
                  }
                  title={isShared ? t("action.unshare") : t("action.share")}
                  loading={
                    loading === FileLoadingEnum.SHARING &&
                    loadingFileId === attachmentId
                  }
                  onClick={() => setShowConfirmSharing(true)}
                />
              )}
              <Button
                size="small"
                type="tertiary"
                iconOnly
                ssrIcon={visibilityShowIcon}
                disabled={!allowPreview}
                title={t("action.preview")}
                loading={
                  loading === FileLoadingEnum.OPENING &&
                  loadingFileId === attachmentId
                }
                onClick={() => onDownload?.("OPEN")}
              />

              <Button
                size="small"
                type="tertiary"
                iconOnly
                title={t("action.download")}
                ssrIcon={arrowDownToLineIcon}
                disabled={!onDownload}
                loading={
                  loading === FileLoadingEnum.DOWNLOADING &&
                  loadingFileId === attachmentId
                }
                onClick={() => onDownload?.("DOWNLOAD")}
              />
              <Button
                size="small"
                type="tertiary"
                iconOnly
                ssrIcon={trashCanIcon}
                disabled={!onDelete}
                title={t("action.delete")}
                loading={
                  loading === FileLoadingEnum.DELETING &&
                  loadingFileId === attachmentId
                }
                onClick={() => onDelete?.(attachmentId)}
              />
            </ButtonsContainer>
          ) : undefined
        }
      />
      <ConfirmModal
        visible={showConfirmSharing}
        onConfirm={() => onShare?.(attachmentId, !isShared)}
        onCancel={() => setShowConfirmSharing(false)}
        onClose={() => setShowConfirmSharing(false)}
        title={isShared ? t("action.unshare") : t("action.share")}
        message={
          isShared
            ? t("prompt.confirm_unshare_attachment")
            : t("prompt.confirm_share_attachment")
        }
      />
    </>
  );
};
